import styled from '@emotion/styled';
import { PortableText } from '@portabletext/react';

import { screen } from '@/components/common/breakpoints';
import { Button } from '@/components/common/Buttons';
import { Image } from '@/components/common/image';
import { Paragraph } from '@/components/common/MarkUp';
import { Surface } from '@/components/common/Surface';
import { Stack } from '@/components/layout/Stack';
import { blockUrlWithAudience } from '@/lib/utils';
import { getTextColour } from '@/sanity/schemas/objects/colours/colourUtils';
import { subheadingSmall } from '@/styles/typography';

import type {
  PullQuote as PullQuoteType,
  SurfaceColourToken,
} from '@/sanity/sanity.types';
import type { SurfaceColourTokenType } from '@/types/colours';
import type { BlockWithAudience, CommonCTAProps } from '@/types/shared';

export interface PullQuoteProps
  extends Omit<PullQuoteType, '_type' | 'CTA'>,
    BlockWithAudience {
  CTA?: CommonCTAProps;
}

type AuthorComponentProps = PullQuoteProps['authorDetails'] & {
  logo?: PullQuoteType['logo'];
  surfaceColourToken?: SurfaceColourTokenType;
};

const AuthorComponent = (authorDetails: AuthorComponentProps) => {
  const {
    authorName,
    image: authorImage,
    jobTitle,
    logo,
    surfaceColourToken,
  } = authorDetails ?? {};

  return (
    authorName && (
      <AuthorSection>
        {authorImage?.asset && (
          <AuthorImageContainer>
            <Image
              {...authorImage}
              alt={logo?.alternateText}
              objectFit="cover"
              radius="full"
            />
          </AuthorImageContainer>
        )}
        <Stack spacing="--space-component-sm">
          <Paragraph size="label-md">{authorName}</Paragraph>
          {jobTitle && (
            <JobTitle size="body-sm" surfaceColour={surfaceColourToken}>
              {jobTitle}
            </JobTitle>
          )}
        </Stack>
      </AuthorSection>
    )
  );
};

export const PullQuote = ({
  layoutType,
  quote,
  logo,
  authorDetails,
  surfaceColour,
  decorationColour,
  CTA,
  pageAudience,
  isAudienceSwitcherEnabled,
}: PullQuoteProps) => {
  const surfaceColourToken = surfaceColour?.token;

  // The decoration colour has been set up as a surface colour in the schema, leave be
  const decorationColourToken: SurfaceColourToken =
    decorationColour?.token || '--surface-galaxy';

  const quoteTextColour =
    decorationColourToken === '--surface-warm-strong'
      ? '--text-strong'
      : decorationColourToken;

  const buttonHref =
    CTA?.url &&
    blockUrlWithAudience(
      CTA.url,
      CTA?.audienceRef,
      pageAudience,
      CTA.noAudienceToggle,
      isAudienceSwitcherEnabled,
    );

  const commonCTA = CTA?.url && (
    <Button
      key={CTA.text}
      size="md"
      iconPosition="right"
      variant="flat-light"
      icon="ChevronRight"
      href={buttonHref}
      aria-label={CTA.accessibleLabel}
      className={
        CTA?.trackingTag?.blockName &&
        `tracking-${CTA.trackingTag.blockName}__${CTA.trackingTag.blockAction}`
      }
    >
      {CTA.text}
    </Button>
  );

  return (
    <>
      {layoutType == 'card' && (
        <Card
          surfaceColour={surfaceColourToken}
          padding="--space-component-xxl"
          borderRadius="--radius-component-xl"
        >
          <Stack spacing="--space-component-xl">
            {logo?.asset && (
              <LogoContainer>
                <Image {...logo} alt={logo?.alternateText} objectFit="cover" />
              </LogoContainer>
            )}
            <QuoteContainer>
              <PortableText value={quote} />
            </QuoteContainer>
            <AuthorComponent
              {...authorDetails}
              logo={logo}
              surfaceColourToken={surfaceColourToken}
            />
            {commonCTA}
          </Stack>
        </Card>
      )}

      {layoutType == 'inline' && (
        <InlineQuote padding={false} decorationColour={decorationColourToken}>
          <Stack spacing="--space-component-xl">
            {logo?.asset && (
              <InlineLogoContainer>
                <Image {...logo} alt={logo?.alternateText} objectFit="cover" />
              </InlineLogoContainer>
            )}
            <QuoteContainer textColour={quoteTextColour}>
              <PortableText value={quote} />
            </QuoteContainer>
            <AuthorComponent {...authorDetails} logo={logo} />
            {commonCTA}
          </Stack>
        </InlineQuote>
      )}
    </>
  );
};

const LogoContainer = styled.div`
  img {
    width: auto;
    height: 48px;
  }
`;
const InlineLogoContainer = styled.div`
  img {
    width: auto;
    height: 56px;
  }
`;
const QuoteContainer = styled.div<{
  textColour?: SurfaceColourToken | '--text-strong';
}>`
  ${subheadingSmall};
  color: ${({ textColour }) => textColour && `var(${textColour})`};
  strong {
    font-weight: var(--font-weight-medium);
  }
`;
const Card = styled(Surface)`
  ${screen.lg} {
    margin-left: var(--space-block-lg);
    margin-right: var(--space-block-lg);
  }
`;
const InlineQuote = styled(Surface)<{
  decorationColour?: SurfaceColourTokenType | '--text-galaxy';
}>`
  width: unset;
  border-left: ${({ decorationColour }) =>
    decorationColour && `4px solid var(${decorationColour})`};
  padding-left: var(--space-component-xxl);
  margin-left: var(--space-block-md);
  ${screen.lg} {
    margin-left: var(--space-block-lg);
  }
`;
const AuthorSection = styled.div`
  display: flex;
  gap: var(--space-component-md);
`;
const AuthorImageContainer = styled.div`
  img {
    width: 48px;
    height: 48px;
  }
`;
const JobTitle = styled(Paragraph)<{ surfaceColour: SurfaceColourTokenType }>`
  color: ${({ surfaceColour }) =>
    surfaceColour
      ? `var(${getTextColour(surfaceColour, { variation: 'base', subTitle: true })})`
      : 'var(--text-warm-base)'};
`;
